import { ref, computed } from '@vue/composition-api'
import { formatObject, searchDirectRetailer } from '@/@core/utils/utils'
import analytics from '@/@core/utils/analytics'
import constants from '@/constants'
import { debounce } from 'lodash'
import store from '@/store'

const { TRACKS } = constants

export function useBrandCart(props) {
  const selectedRetailer = ref(null)
  const retailerId = ref(null)
  const reloadKey = ref(1)
  const notesData = ref({})
  const allDirectRetailers = ref([])
  const searchText = ref('')

  const directRetailers = computed(() => store.state.connections.directRetailers || [])
  const selectedDirectRetailer = computed(() => store.state.shop.selectedDirectRetailer)
  const selectedDirectRetailerTitle = computed(() => {
    const r = store.state.shop.selectedDirectRetailer
    return r ? `${r.entityName} - ${r.email}` : null
  })

  function addDisplayLabel(retailer) {
    return { ...retailer, _displayLabel: `${retailer.entityName} — ${retailer.email}` }
  }

  function initRetailers() {
    allDirectRetailers.value = directRetailers.value.map(addDisplayLabel)
    if (selectedDirectRetailer.value) {
      onSelectRetailer(selectedDirectRetailer.value)
    }
  }

  async function onSelectRetailer(retailer) {
    retailerId.value = retailer.retailerId
    selectedRetailer.value = addDisplayLabel(retailer)
    const discount = retailer?.discount || 1
    if (props.cartProducts) {
      props.cartProducts.forEach(product => {
        product.discount = discount
      })
    }
    searchText.value = ''
    reloadKey.value += 1
    analytics.track(
      TRACKS.ACTIONS.BRAND_SELECTS_RETAILER_FOR_ORDER,
      formatObject({
        retailerId: retailer.retailerId,
        retailerName: retailer.entityName,
      }),
    )
  }

  /**
   * Remove a single product from cartProducts.
   * @returns {boolean} true if cart is now empty (component should close modal)
   */
  function removeProduct(product) {
    if (!props.cartProducts) return false
    const index = props.cartProducts.findIndex(p => p._id === product._id)
    if (index !== -1) {
      props.cartProducts.splice(index, 1)
    }
    analytics.track(
      TRACKS.ACTIONS.BRAND_REMOVES_PRODUCT_FROM_CART,
      formatObject({
        productId: product._id,
        productName: product.name,
      }),
    )
    return props.cartProducts.length === 0
  }

  /**
   * Remove multiple products from cartProducts.
   * @returns {boolean} true if cart is now empty
   */
  function removeMultipleProducts(productIds) {
    if (!props.cartProducts) return false
    productIds.forEach(productId => {
      const index = props.cartProducts.findIndex(p => p._id === productId)
      if (index !== -1) {
        props.cartProducts.splice(index, 1)
      }
    })
    return props.cartProducts.length === 0
  }

  function updateNotes(notesDataArg = {}) {
    notesData.value = {
      notes: notesDataArg.notes,
      isEditNotes: notesDataArg.isEditNotes,
    }
  }

  function searchRetailer() {
    allDirectRetailers.value = searchDirectRetailer(
      directRetailers.value,
      searchText.value,
    ).map(addDisplayLabel)
  }

  const debouncedTrackSearchRetailer = debounce(searchTextVal => {
    analytics.track(TRACKS.ACTIONS.ADD_TO_ORDER_MODAL.BRAND_SEARCHES_FOR_RETAILER, {
      searchText: searchTextVal,
    })
  }, 500)

  return {
    selectedRetailer,
    retailerId,
    reloadKey,
    notesData,
    allDirectRetailers,
    searchText,
    directRetailers,
    selectedDirectRetailer,
    selectedDirectRetailerTitle,
    initRetailers,
    onSelectRetailer,
    removeProduct,
    removeMultipleProducts,
    updateNotes,
    searchRetailer,
    debouncedTrackSearchRetailer,
  }
}
